import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Filter, ExternalLink, X } from 'lucide-react';
import DataGrid from './DataGrid';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * DrillPanel — BI Phase 2 universal drill-down side drawer.
 *
 * Usage:
 *   <DrillDownProvider>
 *     <YourPage/>
 *   </DrillDownProvider>
 *
 *   const { open } = useDrillDown();
 *   open({
 *     type: 'vendor-bills',           // backend route under /api/drilldown/
 *     params: { vendor_name, months },
 *     title: 'Bills — Anil Traders',  // optional override
 *     columns: [...] | 'auto'         // explicit columns or 'auto' for default
 *   });
 *
 * The panel slides in from the right, shows summary stats + a fully
 * sortable/filterable/exportable DataGrid, and supports closing.
 */

const DrillDownContext = createContext({ open: () => {}, close: () => {} });
export const useDrillDown = () => useContext(DrillDownContext);

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// Default column profiles per drill type — when caller passes columns='auto'
const DEFAULT_COLUMNS = {
  'vendor-bills': [
    { key: 'invoice_date', label: 'Date', type: 'date',
      accessor: r => r.invoice_date,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.invoice_date}</span> },
    { key: 'invoice_number', label: 'Invoice', type: 'text',
      accessor: r => r.invoice_number,
      render: r => <span className="font-mono text-amber-400 text-xs">{r.invoice_number}</span> },
    { key: 'products', label: 'Items', type: 'text',
      accessor: r => r.products,
      render: r => <span className="text-zinc-200 text-xs">{r.products}</span> },
    { key: 'qty', label: 'Qty', type: 'number', align: 'right',
      accessor: r => r.qty,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.qty}</span> },
    { key: 'subtotal', label: 'Subtotal', type: 'number', align: 'right',
      accessor: r => r.subtotal,
      render: r => <span className="font-mono text-zinc-300 text-xs">{fmt(r.subtotal)}</span> },
    { key: 'gst_amount', label: 'GST', type: 'number', align: 'right',
      accessor: r => r.gst_amount,
      render: r => <span className="font-mono text-zinc-400 text-xs">{fmt(r.gst_amount)}</span> },
    { key: 'total_amount', label: 'Total', type: 'number', align: 'right',
      accessor: r => r.total_amount,
      render: r => <span className="font-mono text-amber-300 text-xs font-bold">{fmt(r.total_amount)}</span> },
    { key: 'paid_amount', label: 'Paid', type: 'number', align: 'right',
      accessor: r => r.paid_amount,
      render: r => <span className="font-mono text-emerald-300 text-xs">{fmt(r.paid_amount)}</span> },
    { key: 'payment_status', label: 'Pay', type: 'enum',
      accessor: r => r.payment_status,
      render: r => {
        const cls = r.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700'
          : r.payment_status === 'Partial' ? 'bg-amber-900/30 text-amber-300 border-amber-700'
          : 'bg-red-900/30 text-red-300 border-red-700';
        return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{r.payment_status}</span>;
      } },
  ],
  'material-bills': [
    { key: 'invoice_date', label: 'Date', type: 'date',
      accessor: r => r.invoice_date,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.invoice_date}</span> },
    { key: 'vendor_name', label: 'Vendor', type: 'text',
      accessor: r => r.vendor_name,
      render: r => <span className="text-zinc-200 text-xs">{r.vendor_name}</span> },
    { key: 'invoice_number', label: 'Invoice', type: 'text',
      accessor: r => r.invoice_number,
      render: r => <span className="font-mono text-amber-400 text-xs">{r.invoice_number}</span> },
    { key: 'product_name', label: 'Product', type: 'text',
      accessor: r => r.product_name,
      render: r => <span className="text-zinc-200 text-xs">{r.product_name}</span> },
    { key: 'lot_no', label: 'Lot No', type: 'text',
      accessor: r => r.lot_no,
      render: r => r.lot_no
        ? <span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{r.lot_no}</span>
        : <span className="text-zinc-700 text-xs">—</span> },
    { key: 'qty', label: 'Qty', type: 'number', align: 'right',
      accessor: r => r.qty,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.qty} <span className="text-zinc-500">{r.container_type}</span></span> },
    { key: 'unit_price', label: 'Rate', type: 'number', align: 'right',
      accessor: r => r.unit_price,
      render: r => <span className="font-mono text-zinc-300 text-xs">{fmt(r.unit_price)}</span> },
    { key: 'total_amount', label: 'Total', type: 'number', align: 'right',
      accessor: r => r.total_amount,
      render: r => <span className="font-mono text-amber-300 text-xs font-bold">{fmt(r.total_amount)}</span> },
  ],
  'material-batches': [
    { key: 'created_at', label: 'Date', type: 'date',
      accessor: r => r.created_at,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.created_at}</span> },
    { key: 'batch_number', label: 'Batch', type: 'text',
      accessor: r => r.batch_number,
      render: r => <span className="font-mono text-amber-400 text-xs">{r.batch_number}</span> },
    { key: 'product_name', label: 'Product', type: 'text',
      accessor: r => r.product_name,
      render: r => <span className="text-zinc-200 text-xs">{r.product_name}</span> },
    { key: 'for_client', label: 'Client', type: 'text',
      accessor: r => r.for_client,
      render: r => <span className="text-sky-300 text-xs">{r.for_client}</span> },
    { key: 'status', label: 'Status', type: 'enum',
      accessor: r => r.status,
      render: r => <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{r.status}</span> },
    { key: 'qty_consumed', label: 'Qty Consumed', type: 'number', align: 'right',
      accessor: r => r.qty_consumed,
      render: r => <span className="font-mono text-amber-300 text-xs font-bold">{r.qty_consumed} kg</span> },
    { key: 'lot_no', label: 'From Lot', type: 'text',
      accessor: r => r.lot_no,
      render: r => r.lot_no
        ? <span className="font-mono text-[10px] bg-amber-900/30 text-amber-300 border border-amber-700/40 px-1.5 py-0.5 rounded">{r.lot_no}</span>
        : <span className="text-zinc-700 text-xs">—</span> },
  ],
  'client-orders': [
    { key: 'created_at', label: 'Date', type: 'date',
      accessor: r => r.created_at,
      render: r => <span className="font-mono text-zinc-300 text-xs">{r.created_at}</span> },
    { key: 'order_no', label: 'Order', type: 'text',
      accessor: r => r.order_no,
      render: r => <span className="font-mono text-amber-400 text-xs">{r.order_no}</span> },
    { key: 'items_summary', label: 'Items', type: 'text',
      accessor: r => r.items_summary,
      render: r => <span className="text-zinc-200 text-xs">{r.items_summary}</span> },
    { key: 'total_amount', label: 'Amount', type: 'number', align: 'right',
      accessor: r => r.total_amount,
      render: r => <span className="font-mono text-emerald-300 text-xs font-bold">{fmt(r.total_amount)}</span> },
    { key: 'due_date', label: 'Due', type: 'date',
      accessor: r => r.due_date,
      render: r => <span className="font-mono text-zinc-400 text-xs">{r.due_date || '—'}</span> },
    { key: 'status', label: 'Status', type: 'enum',
      accessor: r => r.status,
      render: r => <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-zinc-700">{r.status}</span> },
    { key: 'payment_status', label: 'Pay', type: 'enum',
      accessor: r => r.payment_status,
      render: r => {
        const cls = r.payment_status === 'Paid' ? 'bg-green-900/40 text-green-300 border-green-700'
          : r.payment_status === 'Partial' ? 'bg-amber-900/30 text-amber-300 border-amber-700'
          : 'bg-red-900/30 text-red-300 border-red-700';
        return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>{r.payment_status}</span>;
      } },
  ],
};

export function DrillDownProvider({ children }) {
  const [drill, setDrill] = useState(null);   // { type, params, title?, columns? }
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);
  const isOpen = !!drill;

  const open = (config) => {
    setData(null);
    setDrill(config);
  };
  const close = () => { setDrill(null); setData(null); };

  useEffect(() => {
    if (!drill) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/drilldown/${drill.type}`, { params: drill.params, withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) toast.error(e.response?.data?.detail || 'Drill-down failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [drill]);

  const columns = drill?.columns && drill.columns !== 'auto'
    ? drill.columns
    : (drill?.type ? DEFAULT_COLUMNS[drill.type] : []);

  return (
    <DrillDownContext.Provider value={{ open, close }}>
      {children}
      <Sheet open={isOpen} onOpenChange={(v) => { if (!v) close(); }}>
        <SheetContent side="right" className="w-full sm:max-w-4xl !max-w-4xl bg-zinc-950 border-zinc-800 overflow-y-auto" data-testid="drill-panel">
          {drill && (
            <>
              <SheetHeader className="space-y-2 border-b border-zinc-800 pb-3">
                <SheetTitle className="text-zinc-100 text-base flex items-center gap-2">
                  {drill.title || data?.title || 'Drill Down'}
                </SheetTitle>
                <SheetDescription className="flex items-center gap-1.5 flex-wrap text-xs">
                  {(data?.filters || []).map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded text-zinc-400">
                      <Filter size={9} className="text-amber-400"/> {f}
                    </span>
                  ))}
                </SheetDescription>
              </SheetHeader>

              {loading && <div className="py-10 text-center text-zinc-500 text-sm">Loading details…</div>}

              {!loading && data && (
                <div className="mt-4 space-y-3">
                  {/* Summary stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(data.summary || {}).map(([k, v]) => (
                      <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2" data-testid={`drill-stat-${k}`}>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k.replace(/_/g, ' ')}</div>
                        <div className="text-base font-bold text-zinc-100 mt-0.5 font-mono">
                          {typeof v === 'number' && (k.includes('amount') || k.includes('cost')) ? fmt(v) : v}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Underlying rows */}
                  <DataGrid
                    testId="drill-grid"
                    rows={data.rows || []}
                    columns={columns || []}
                    rowKey={(r, i) => r.bill_id || r.order_no || r.batch_number || i}
                    pageSize={50}
                    exportName={`drill-${drill.type}`}
                    emptyText="No underlying records found"
                  />

                  {drill.openInFullHref && (
                    <a href={drill.openInFullHref}
                      className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 underline"
                      data-testid="drill-open-full">
                      Open in full page <ExternalLink size={10}/>
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </DrillDownContext.Provider>
  );
}

// Helper component that wraps a cell value with a click-to-drill action.
export function DrillCell({ drill, children, className = '', testId }) {
  const { open } = useDrillDown();
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); open(drill); }}
      className={`text-left hover:underline decoration-amber-500 decoration-dotted underline-offset-2 ${className}`}
      data-testid={testId}
      title="Click to drill down"
    >
      {children}
    </button>
  );
}
